import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, FileText, Play, Stethoscope, TriangleAlert } from 'lucide-react';
import { logLines } from '../runLogBuffer';
import { diagnoseFailure, itemForDiagnosis } from './runFailures';
import { fixLines, hintsFor } from './doctorModel';
import { CommandLine } from './RunDoctorPanel';

/** A run that ended badly (not one the user stopped, not a setup command). */
export function failedRun(run) {
  if (!run || run.task) return false;
  if (run.status === 'exited') return run.exitCode != null && run.exitCode !== 0;
  return run.status === 'error' && /^not_found/.test(String(run.error || ''));
}

/**
 * Under a failed run row: the cause read from its log, and the fix —
 * Install / Install & run for missing dependencies (Install & run starts
 * the configuration only after the install succeeded), the install hint for
 * a missing toolchain, "Create .env" for a missing variable, Check setup
 * for a database that isn't reachable.
 */
export default function RunFailureBanner({ run, config, report, onInstall, onCheck, onOpenFile }) {
  const { t } = useTranslation();
  const diagnosis = useMemo(
    () => diagnoseFailure({ lines: logLines(run.log || { lines: [], partial: '' }), exitCode: run.exitCode, error: run.error, config }),
    [run.log, run.exitCode, run.error, config]
  );
  const item = useMemo(() => itemForDiagnosis(diagnosis, report, config), [diagnosis, report, config]);
  // Fixed since (Check setup now says ✓): the cause is gone, so is the banner.
  if (!diagnosis || item?.status === 'ok') return null;
  const fix = item?.fix;
  const runnable = fix && (fix.commands?.length || fix.action === 'env_copy');
  const isDeps = diagnosis.kind === 'deps';
  const hints = item ? hintsFor(item) : [];
  return (
    <div className="pj-run-banner" role="alert" data-testid="run-failure-banner" data-cause={diagnosis.cause}>
      <TriangleAlert size={14} className="pj-run-banner-icon" aria-hidden />
      <div className="pj-run-banner-body">
        <span className="pj-run-banner-title">{t(`projects.run.doctor.causes.${diagnosis.cause}`, diagnosis.params)}</span>
        {runnable && fixLines(item).map((l) => <CommandLine key={l} text={l} testId="run-failure-command" />)}
        {hints.length > 0 && hints.map((h) => <CommandLine key={h.command} text={h.command} testId="run-failure-hint" />)}
      </div>
      <div className="pj-run-banner-actions">
        {runnable && fix.action !== 'env_copy' && (
          <>
            <button type="button" className="gd-btn" onClick={() => onInstall(item, false)} data-testid="run-install">
              <Download size={13} /> {isDeps ? t('projects.run.doctor.install') : t('projects.run.doctor.fix.run')}
            </button>
            <button type="button" className="gd-btn gd-btn--primary" onClick={() => onInstall(item, true)} data-testid="run-install-and-run">
              <Play size={13} /> {isDeps ? t('projects.run.doctor.installAndRun') : t('projects.run.doctor.runAndStart')}
            </button>
          </>
        )}
        {fix?.action === 'env_copy' && (
          <button type="button" className="gd-btn" onClick={() => onInstall(item, false)} data-testid="run-install">
            <FileText size={13} /> {t('projects.run.doctor.fix.envCopy')}
          </button>
        )}
        {fix?.action === 'open_file' && (
          <button type="button" className="gd-btn" onClick={() => onOpenFile(fix.file)} data-testid="run-open-env">
            <FileText size={13} /> {t('projects.run.doctor.fix.openFile', { file: fix.file.split('/').pop() })}
          </button>
        )}
        {!runnable && fix?.action !== 'open_file' && (
          <button type="button" className="gd-btn" onClick={onCheck} data-testid="run-check-setup">
            <Stethoscope size={13} /> {t('projects.run.doctor.check')}
          </button>
        )}
      </div>
    </div>
  );
}
